// AgentService源码统一命名；wire版本与浏览器偏好键保留既有值，确保历史会话兼容。
/** 客服会话语种边界；不读取或修改全站i18n、当前角色和其他会话。 */
export const AGENT_SERVICE_LANGUAGE_CONTRACT_VERSION =
  "customer-service.language.v1";
export const AGENT_SERVICE_LANGUAGE_FIXTURE_SHA256 =
  "e36fc7c53c4181f954cc6656d0fd4c18a7154b0858f835e36bfee910dc467839";
/** 旧榜单只保留历史读取和任务执行兼容，不进入新语言选择列表。 */
export const AGENT_SERVICE_LEGACY_LANGUAGE_OPTIONS = Object.freeze(
  [
    { value: "en", label: "英语", nativeLabel: "English" },
    { value: "zh-CN", label: "简体中文", nativeLabel: "简体中文" },
    { value: "hi", label: "印地语", nativeLabel: "हिन्दी" },
    { value: "es", label: "西班牙语", nativeLabel: "Español" },
    { value: "ar", label: "现代标准阿拉伯语", nativeLabel: "العربية الفصحى" },
    { value: "fr", label: "法语", nativeLabel: "Français" },
    { value: "bn", label: "孟加拉语", nativeLabel: "বাংলা" },
    { value: "pt", label: "葡萄牙语", nativeLabel: "Português" },
    { value: "id", label: "印度尼西亚语", nativeLabel: "Bahasa Indonesia" },
    { value: "ur", label: "乌尔都语", nativeLabel: "اردو" },
    { value: "ru", label: "俄语", nativeLabel: "Русский" },
    { value: "de", label: "德语", nativeLabel: "Deutsch" },
    { value: "ja", label: "日语", nativeLabel: "日本語" },
    { value: "pcm", label: "尼日利亚皮钦语", nativeLabel: "Naijá" },
    { value: "ar-EG", label: "埃及阿拉伯语", nativeLabel: "العربية المصرية" },
    { value: "mr", label: "马拉地语", nativeLabel: "मराठी" },
    { value: "vi", label: "越南语", nativeLabel: "Tiếng Việt" },
    { value: "te", label: "泰卢固语", nativeLabel: "తెలుగు" },
    { value: "sw", label: "斯瓦希里语", nativeLabel: "Kiswahili" },
    { value: "ha", label: "豪萨语", nativeLabel: "Hausa" },
  ].map((item) => Object.freeze(item))
);
/** 半导体客户、供应链及东南亚仓储优先；与TS和Java新偏好白名单一致。 */
export const AGENT_SERVICE_LANGUAGE_OPTIONS = Object.freeze(
  [
    { value: "zh-CN", label: "简体中文", nativeLabel: "简体中文" },
    { value: "en", label: "英语", nativeLabel: "English" },
    { value: "ko", label: "韩语", nativeLabel: "한국어" },
    { value: "ja", label: "日语", nativeLabel: "日本語" },
    { value: "zh-TW", label: "繁体中文", nativeLabel: "繁體中文" },
    { value: "ru", label: "俄语", nativeLabel: "Русский" },
    { value: "de", label: "德语", nativeLabel: "Deutsch" },
    { value: "fr", label: "法语", nativeLabel: "Français" },
    { value: "nl", label: "荷兰语", nativeLabel: "Nederlands" },
    { value: "it", label: "意大利语", nativeLabel: "Italiano" },
    { value: "es", label: "西班牙语", nativeLabel: "Español" },
    { value: "vi", label: "越南语", nativeLabel: "Tiếng Việt" },
    { value: "th", label: "泰语", nativeLabel: "ไทย" },
    { value: "ms", label: "马来语", nativeLabel: "Bahasa Melayu" },
    { value: "id", label: "印度尼西亚语", nativeLabel: "Bahasa Indonesia" },
  ].map((item) => Object.freeze(item))
);
export const AGENT_SERVICE_COMPATIBLE_LANGUAGE_OPTIONS = Object.freeze([
  ...AGENT_SERVICE_LANGUAGE_OPTIONS,
  ...AGENT_SERVICE_LEGACY_LANGUAGE_OPTIONS.filter(
    (legacy) =>
      !AGENT_SERVICE_LANGUAGE_OPTIONS.some(
        (active) => active.value === legacy.value
      )
  ),
]);
export const AGENT_SERVICE_SUPPORTED_LANGUAGES = Object.freeze(
  AGENT_SERVICE_LANGUAGE_OPTIONS.map((item) => item.value)
);
export const AGENT_SERVICE_TRANSLATION_MODES = Object.freeze([
  "single",
  "bilingual",
]);
const AGENT_SERVICE_TRANSLATION_MODE_STORAGE_KEY =
  "xsou.customer-service.translation-mode.v1";

export const normalizeAgentServiceTranslationMode = (value) =>
  AGENT_SERVICE_TRANSLATION_MODES.includes(value) ? value : "bilingual";

/**
 * 单双语只是当前浏览器标签页的展示偏好：不写业务库、不改会话语种、
 * 不触发历史重译。sessionStorage让总客服、商品客服和消息中心在组件重建后保持一致。
 */
export const readAgentServiceTranslationMode = (storage) => {
  const target =
    storage === undefined && typeof window !== "undefined"
      ? window.sessionStorage
      : storage;
  try {
    return normalizeAgentServiceTranslationMode(
      target?.getItem(AGENT_SERVICE_TRANSLATION_MODE_STORAGE_KEY)
    );
  } catch {
    return "bilingual";
  }
};

export const saveAgentServiceTranslationMode = (value, storage) => {
  const normalized = normalizeAgentServiceTranslationMode(value);
  const target =
    storage === undefined && typeof window !== "undefined"
      ? window.sessionStorage
      : storage;
  try {
    target?.setItem(AGENT_SERVICE_TRANSLATION_MODE_STORAGE_KEY, normalized);
  } catch {
    // 隐私模式或存储被禁用时，父组件仍保留本次运行内状态。
  }
  return normalized;
};
export const agentServiceLanguageLabel = (language) => {
  const item = AGENT_SERVICE_COMPATIBLE_LANGUAGE_OPTIONS.find(
    (option) => option.value === language
  );
  return item?.nativeLabel || language;
};

/** 标题栏使用两至三字符短标签；完整语种名仍用于语言列表、提示及无障碍描述。 */
export const agentServiceLanguageShortLabel = (language) => {
  const labels = { "zh-CN": "简中", "zh-TW": "繁中", "zh-HK": "繁中", en: "EN", ko: "한국어", ja: "日本語", th: "ไทย" };
  return labels[language] || String(language || "").split("-")[0].toUpperCase().slice(0, 3);
};
const commonKeys = [
  "schemaVersion",
  "contractVersion",
  "tenantId",
  "conversationId",
];
const messageKeys = [
  ...commonKeys,
  "translationRequestId",
  "businessMessageId",
  "snapshot",
];
const translationKeys = [
  ...messageKeys,
  "status",
  "translatedContent",
  "detectedSourceLanguage",
  "modelReleaseId",
  "promptVersion",
  "completedAt",
  "errorCode",
];

/** 严格读取补充契约，拒绝客户端或旧接口混入未知字段。 */
const record = (value, keys) => {
  if (
    !value ||
    typeof value !== "object" ||
    Array.isArray(value) ||
    Object.keys(value).some((key) => !keys.includes(key))
  )
    throw new Error("客服语言契约字段不合法。");
  return value;
};
const text = (value, max = 128) => {
  if (typeof value !== "string" || !value.trim() || value.length > max)
    throw new Error("客服语言文本或标识不合法。");
  return value;
};
const language = (value) => {
  if (
    !AGENT_SERVICE_COMPATIBLE_LANGUAGE_OPTIONS.some(
      (item) => item.value === value
    )
  )
    throw new Error("客服会话语言不在开放名单中。");
  return value;
};
const selectableLanguage = (value) => {
  if (!AGENT_SERVICE_SUPPORTED_LANGUAGES.includes(value))
    throw new Error("客服会话语言不在当前业务开放名单中。");
  return value;
};
const version = (value) => {
  if (!Number.isSafeInteger(value) || value < 1)
    throw new Error("客服语言版本必须是安全正整数。");
};
const time = (value) => {
  if (
    typeof value !== "string" ||
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(
      value
    ) ||
    !Number.isFinite(Date.parse(value))
  )
    throw new Error("客服语言时间必须带时区。");
};
const common = (value) => {
  if (
    value.schemaVersion !== 1 ||
    value.contractVersion !== AGENT_SERVICE_LANGUAGE_CONTRACT_VERSION
  )
    throw new Error("客服语言契约版本不支持。");
  text(value.tenantId, 64);
  text(value.conversationId);
};
const snapshot = (value) => {
  record(value, [
    "customerLanguage",
    "languageVersion",
    "targetLanguage",
    "originalContentSha256",
  ]);
  language(value.customerLanguage);
  language(value.targetLanguage);
  version(value.languageVersion);
  if (
    typeof value.originalContentSha256 !== "string" ||
    !/^[a-f0-9]{64}$/.test(value.originalContentSha256)
  )
    throw new Error("原文摘要不合法。");
};
const messageReference = (value) => {
  common(value);
  text(value.translationRequestId);
  text(value.businessMessageId);
  snapshot(value.snapshot);
};

/** 默认ENV只用于首次默认值；会话已有偏好必须由Spring读取。 */
export const readAgentServiceDefaultLanguage = (configuredDefault) =>
  selectableLanguage(
    configuredDefault === undefined || configuredDefault === ""
      ? "zh-CN"
      : configuredDefault
  );
export const adaptCustomerLanguageChangeRequest = (value) => {
  record(value, ["customerLanguage", "expectedLanguageVersion"]);
  selectableLanguage(value.customerLanguage);
  version(value.expectedLanguageVersion);
  return { ...value };
};
export const adaptCustomerLanguagePreference = (value) => {
  record(value, [
    ...commonKeys,
    "customerLanguage",
    "languageVersion",
    "updatedAt",
  ]);
  common(value);
  language(value.customerLanguage);
  version(value.languageVersion);
  time(value.updatedAt);
  return { ...value };
};
export const adaptCustomerTranslationTask = (value) => {
  record(value, [...messageKeys, "eventType", "sceneType", "occurredAt"]);
  messageReference(value);
  if (
    value.eventType !== "customer.message.translation.requested.v1" ||
    !["PRODUCT", "ORDER", "GENERAL"].includes(value.sceneType)
  )
    throw new Error("翻译事件或场景不合法。");
  time(value.occurredAt);
  return { ...value, snapshot: { ...value.snapshot } };
};
/** 读取受鉴权的任务正文，方向必须对应原作者。 */
export const adaptCustomerTranslationTaskContent = (value) => {
  record(value, ["task", "originalContent", "actorType"]);
  const task = adaptCustomerTranslationTask(value.task);
  text(value.originalContent, 20000);
  if (!["CUSTOMER", "STAFF", "AI"].includes(value.actorType))
    throw new Error("翻译作者不合法。");
  const target =
    value.actorType === "CUSTOMER" ? "zh-CN" : task.snapshot.customerLanguage;
  if (target !== task.snapshot.targetLanguage)
    throw new Error("原文作者与冻结翻译方向不一致。");
  return { ...value, task };
};
export const adaptCustomerTranslationView = (value) => {
  record(value, translationKeys);
  messageReference(value);
  if (
    !["PENDING", "SUCCEEDED", "FAILED", "NOT_REQUIRED"].includes(value.status)
  )
    throw new Error("译文状态不合法。");
  const success = value.status === "SUCCEEDED";
  if (
    success !== (value.translatedContent !== undefined) ||
    (value.status !== "PENDING") !== (value.completedAt !== undefined) ||
    (value.status === "FAILED") !== (value.errorCode !== undefined)
  )
    throw new Error("译文状态和内容不一致。");
  if (success) {
    text(value.translatedContent, 20000);
    text(value.modelReleaseId);
    text(value.promptVersion);
    if (
      typeof value.detectedSourceLanguage !== "string" ||
      value.detectedSourceLanguage.length > 35 ||
      !/^[A-Za-z]{2,8}(?:-[A-Za-z0-9]{1,8})*$/.test(
        value.detectedSourceLanguage
      )
    )
      throw new Error("检测源语言不合法。");
  } else if (
    ["detectedSourceLanguage", "modelReleaseId", "promptVersion"].some(
      (key) => value[key] !== undefined
    )
  )
    throw new Error("非成功译文不得带成功元数据。");
  if (value.completedAt !== undefined) time(value.completedAt);
  if (
    value.errorCode !== undefined &&
    ![
      "TRANSLATION_UNAVAILABLE",
      "TRANSLATION_INVALID_OUTPUT",
      "TRANSLATION_FACT_MISMATCH",
    ].includes(value.errorCode)
  )
    throw new Error("翻译错误码不合法。");
  return { ...value, snapshot: { ...value.snapshot } };
};
export const adaptCustomerTranslationResult = (value) => {
  const result = adaptCustomerTranslationView(value);
  if (result.status === "PENDING") throw new Error("回调只能报告翻译终态。");
  return result;
};
/** 当前历史页有界补读，旧消息缺条目即无附加译文。 */
export const adaptCustomerTranslationPage = (value) => {
  record(value, [...commonKeys, "items"]);
  common(value);
  if (!Array.isArray(value.items) || value.items.length > 50)
    throw new Error("译文页最多50条。");
  const seen = new Set();
  const items = value.items.map((item) => {
    const result = adaptCustomerTranslationView(item);
    if (
      result.tenantId !== value.tenantId ||
      result.conversationId !== value.conversationId ||
      seen.has(result.businessMessageId)
    )
      throw new Error("译文页包含跨会话或重复消息。");
    seen.add(result.businessMessageId);
    return result;
  });
  return { ...value, items };
};
export const adaptCustomerLanguageNotification = (value) => {
  record(value, [
    ...commonKeys,
    "type",
    "customerLanguage",
    "languageVersion",
    "occurredAt",
  ]);
  common(value);
  if (value.type !== "conversation.language.changed")
    throw new Error("语言通知类型不合法。");
  language(value.customerLanguage);
  version(value.languageVersion);
  time(value.occurredAt);
  return { ...value };
};
export const adaptCustomerTranslationNotification = (value) => {
  record(value, [
    ...commonKeys,
    "type",
    "businessMessageId",
    "translationRequestId",
    "languageVersion",
    "status",
    "occurredAt",
  ]);
  common(value);
  text(value.businessMessageId);
  text(value.translationRequestId);
  version(value.languageVersion);
  if (
    value.type !== "message.translation.updated" ||
    !["SUCCEEDED", "FAILED", "NOT_REQUIRED"].includes(value.status)
  )
    throw new Error("译文通知类型或状态不合法。");
  time(value.occurredAt);
  return { ...value };
};

/** 临时和正式语言来源均实现此端口；这里不创建HTTP、订阅或浏览器持久化。 */
export const createAgentServiceLanguageAdapter = (
  source,
  configuredDefault
) => {
  if (
    !source ||
    ["read", "update", "subscribe"].some(
      (key) => typeof source[key] !== "function"
    )
  )
    throw new Error("客服语言来源端口不完整。");
  const requireConversation = (conversationId, value) => {
    if (value.conversationId !== text(conversationId))
      throw new Error("客服语言来源返回了其他会话。");
    return value;
  };
  return Object.freeze({
    defaultLanguage: readAgentServiceDefaultLanguage(configuredDefault),
    supportedLanguages: AGENT_SERVICE_SUPPORTED_LANGUAGES,
    read: async (conversationId) =>
      requireConversation(
        conversationId,
        adaptCustomerLanguagePreference(await source.read(text(conversationId)))
      ),
    update: async (conversationId, request) => {
      const change = adaptCustomerLanguageChangeRequest(request);
      const result = requireConversation(
        conversationId,
        adaptCustomerLanguagePreference(
          await source.update(text(conversationId), change)
        )
      );
      if (
        result.customerLanguage !== change.customerLanguage ||
        ![
          change.expectedLanguageVersion,
          change.expectedLanguageVersion + 1,
        ].includes(result.languageVersion)
      )
        throw new Error("客服语言更新回执不匹配。");
      return result;
    },
    subscribe: (conversationId, listener) => {
      text(conversationId);
      if (typeof listener !== "function")
        throw new Error("客服语言通知监听器不合法。");
      // 即使来源已经排队了通知，注销之后也不能送入旧账号/旧窗口；取消必须幂等。
      let active = true;
      const unsubscribe = source.subscribe(conversationId, (event) => {
        if (!active) return;
        listener(
          requireConversation(
            conversationId,
            adaptCustomerLanguageNotification(event)
          )
        );
      });
      if (typeof unsubscribe !== "function") {
        active = false;
        throw new Error("客服语言来源必须支持取消订阅。");
      }
      return () => {
        if (!active) return;
        active = false;
        unsubscribe();
      };
    },
  });
};
